// P2.4 — typed wrapper around the resolve-auth-state edge function.
//
// Every auth entry point (login, accept-invite, reset-password) calls this
// FIRST, before any decisional rendering, so we route on server-truth state
// and never send a user into a screen whose action will fail.
//
// The function returns a fixed shape even for unknown / rate-limited /
// malformed emails (byte-identical body), so the caller MUST NOT branch on
// anything beyond `screen_key` and `memberships`.

import { supabase } from "@/integrations/supabase/client";

export type MembershipAction =
  | "accept_invite"
  | "enter_scope"
  | "choose_scope";

export type Membership = {
  scope_kind: string;
  scope_id: string;
  scope_label: string;
  role: string;
  action: MembershipAction | string;
};

export type ResolvedScreenKey =
  | "login"
  | "invite_expired"
  | "account_setup"
  | "reset_pending"
  | "reset_expired"
  | "account_disabled"
  | "account_locked"
  | "check_email"
  | "dashboard"
  | "tenant_picker";

export type ResolvedAuthState = {
  account_state: string;
  screen_key: ResolvedScreenKey | string;
  forward_action: string;
  memberships: Membership[];
  meta: Record<string, unknown>;
};

const FALLBACK: ResolvedAuthState = {
  account_state: "active",
  screen_key: "login",
  forward_action: "password_sign_in",
  memberships: [],
  meta: {},
};

export async function resolveAuthState(email: string): Promise<ResolvedAuthState> {
  try {
    const { data, error } = await supabase.functions.invoke("resolve-auth-state", {
      body: { email },
    });
    if (error || !data) return FALLBACK;
    return {
      ...FALLBACK,
      ...(data as ResolvedAuthState),
      memberships: Array.isArray((data as any).memberships) ? (data as any).memberships : [],
      meta: (data as any).meta ?? {},
    };
  } catch {
    return FALLBACK;
  }
}
